import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { adminPackagesAPI, adminCustomersAPI, adminStatsAPI, shippingRatesAPI, shippingCalcAPI } from '../lib/api';
import api from '../lib/api';
import Navbar from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import DealsAdmin from '../components/admin/DealsAdmin';
import AnalyticsWidget from '../components/admin/AnalyticsWidget';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Package, 
  Users, 
  TrendingUp, 
  Truck,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  DollarSign,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Copy,
  X,
  Building2,
  MessageSquare,
  Clock,
  CheckCircle,
  ExternalLink,
  FileText,
  Ship,
  Car,
  Home,
  Image as ImageIcon,
  Megaphone,
  Send,
  Eye,
  Layers,
  AlertTriangle,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { packageStatusConfig, formatDate, formatCurrency, allCountries, countryNames } from '../lib/utils';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [rates, setRates] = useState([]);
  const [proQuotes, setProQuotes] = useState([]);
  const [fretQuotes, setFretQuotes] = useState([]);
  const [fretFilter, setFretFilter] = useState('all');
  const [selectedFretQuote, setSelectedFretQuote] = useState(null);
  // Marketing
  const [mkAudience, setMkAudience] = useState({ all: 0, inactive_30d: 0, inactive_60d: 0, active_only: 0 });
  const [mkCampaigns, setMkCampaigns] = useState([]);
  const [mkTarget, setMkTarget] = useState('all');
  const [mkPreviewHtml, setMkPreviewHtml] = useState('');
  const [mkSending, setMkSending] = useState(false);
  // Groupage admin
  const [adminGroups, setAdminGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState('all');
  const [storageOverview, setStorageOverview] = useState({ summary: {}, packages: [] });
  const [loading, setLoading] = useState(true);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Manual grouping state
  const [selectedPkgIds, setSelectedPkgIds] = useState([]);
  const [grouperOpen, setGrouperOpen] = useState(false);
  const [grouperName, setGrouperName] = useState('');
  const [grouperPrice, setGrouperPrice] = useState('');
  const [grouperNote, setGrouperNote] = useState('');
  const [grouperSubmitting, setGrouperSubmitting] = useState(false);

  // Group row actions (inline in packages tab)
  const [groupShipOpen, setGroupShipOpen] = useState(false);
  const [groupShipTarget, setGroupShipTarget] = useState(null);
  const [groupShipTracking, setGroupShipTracking] = useState('');
  const [groupShipSubmitting, setGroupShipSubmitting] = useState(false);
  const [groupDetailOpen, setGroupDetailOpen] = useState(false);
  const [groupDetailTarget, setGroupDetailTarget] = useState(null);
  
  // Customer lookup state
  const [customerLookup, setCustomerLookup] = useState('');
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        adminStatsAPI.getStats(),
        adminPackagesAPI.getAll(),
        adminCustomersAPI.getAll(),
        shippingRatesAPI.getAll(),
        api.get('/admin/pro-quotes'),
        api.get('/admin/fret-quotes'),
        api.get('/admin/groups'),
        api.get('/admin/storage-overview'),
        api.get('/admin/customers-with-subscriptions'),
      ]);

      const getData = (idx, fallback) => {
        const r = results[idx];
        if (r.status === 'fulfilled' && r.value?.data !== undefined) return r.value.data;
        if (r.status === 'rejected') {
          console.warn(`[Admin fetchData] request ${idx} failed:`, r.reason?.message || r.reason);
        }
        return fallback;
      };

      setStats(getData(0, {}));
      setPackages(getData(1, []));
      setCustomers(getData(2, []));
      setRates(getData(3, []));
      setProQuotes(getData(4, []));
      setFretQuotes(getData(5, []));
      setAdminGroups(getData(6, []));
      setStorageOverview(getData(7, { summary: {}, packages: [] }));
      setSubscribers(getData(8, []));

      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) {
        toast.warning(`${failed} section(s) du dashboard n'ont pas pu charger. Les autres sont disponibles.`);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const seedDefaultRates = async () => {
    try {
      await adminStatsAPI.seedRates();
      toast.success('Tarifs par défaut créés');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création des tarifs');
    }
  };

  // Customer lookup function
  const handleCustomerLookup = async () => {
    if (!customerLookup.trim()) {
      toast.error('Entrez un numéro client');
      return;
    }
    
    setLookupLoading(true);
    setFoundCustomer(null);
    
    try {
      const res = await adminCustomersAPI.searchByNumber(customerLookup.trim().toUpperCase());
      setFoundCustomer(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Client non trouvé');
      } else {
        toast.error('Erreur lors de la recherche');
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const filteredPackages = packages.filter(pkg => {
    if (filterStatus !== 'all' && pkg.status !== filterStatus) return false;
    if (searchCustomer && !pkg.customer_number.toLowerCase().includes(searchCustomer.toLowerCase()) && 
        !pkg.customer_name.toLowerCase().includes(searchCustomer.toLowerCase())) return false;
    return true;
  });

  // Fuse grouped packages into a single "group row" - one row per group, plus standalone packages
  // ROBUST: we use BOTH pkg.group_id AND group.package_ids (some legacy groups have package_ids but
  // packages without group_id due to an older backend bug)
  const activeGroups = (adminGroups || []).filter(g => g.status !== 'cancelled');
  const groupsById = activeGroups.reduce((acc, g) => { acc[g.id] = g; return acc; }, {});
  const pkgIdToGroup = activeGroups.reduce((acc, g) => {
    (g.package_ids || []).forEach(pid => { if (!acc[pid]) acc[pid] = g; });
    return acc;
  }, {});
  const displayItems = (() => {
    const seenGroups = new Set();
    const items = [];
    for (const pkg of filteredPackages) {
      const linkedGroup = (pkg.group_id && groupsById[pkg.group_id]) || pkgIdToGroup[pkg.id];
      if (linkedGroup) {
        if (seenGroups.has(linkedGroup.id)) continue;
        seenGroups.add(linkedGroup.id);
        // Collect ALL packages of this group from BOTH sources
        const idsInGroup = new Set(linkedGroup.package_ids || []);
        const groupPkgs = packages.filter(p => p.group_id === linkedGroup.id || idsInGroup.has(p.id));
        items.push({ type: 'group', group: linkedGroup, pkgs: groupPkgs });
      } else {
        items.push({ type: 'package', pkg });
      }
    }
    return items;
  })();

  const groupStatusConfig = {
    submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-800' },
    approved: { label: 'Prêt à expédier', color: 'bg-emerald-100 text-emerald-800' },
    preparing: { label: 'En préparation', color: 'bg-amber-100 text-amber-800' },
    shipped: { label: 'Expédié', color: 'bg-purple-100 text-purple-800' },
    delivered: { label: 'Livré', color: 'bg-slate-100 text-slate-700' },
    cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
  };

  const openShipGroup = (g) => {
    setGroupShipTarget(g);
    setGroupShipTracking(g.tracking_number || '');
    setGroupShipOpen(true);
  };

  const confirmShipGroup = async () => {
    if (!groupShipTarget) return;
    if (!groupShipTracking.trim()) {
      toast.error('Indiquez un numéro de suivi');
      return;
    }
    setGroupShipSubmitting(true);
    try {
      await api.patch(`/admin/groups/${groupShipTarget.id}/status`, {
        status: 'shipped',
        tracking_number: groupShipTracking.trim(),
      });
      toast.success('Groupage expédié ! Les colis ont été marqués comme expédiés.');
      setGroupShipOpen(false);
      setGroupShipTarget(null);
      setGroupShipTracking('');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'expédition');
    } finally {
      setGroupShipSubmitting(false);
    }
  };

  const cancelGroup = async (g) => {
    if (!window.confirm(`Dégrouper "${g.name}" ? Les ${g.package_ids?.length || 0} colis redeviendront indépendants.`)) return;
    try {
      await api.patch(`/admin/groups/${g.id}/status`, { status: 'cancelled' });
      toast.success('Groupage annulé. Les colis sont à nouveau disponibles.');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const markGroupDelivered = async (g) => {
    if (!window.confirm(`Marquer "${g.name}" comme livré ?`)) return;
    try {
      await api.patch(`/admin/groups/${g.id}/status`, { status: 'delivered' });
      toast.success('Groupage marqué comme livré');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const resendGroupPaymentEmail = async (g) => {
    try {
      await api.post(`/admin/groups/${g.id}/resend-payment-email`);
      toast.success(`Email de paiement renvoyé à ${g.client_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <Navbar />
      
      <main className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-3xl text-slate-900">
                Administration
              </h1>
              <p className="text-slate-600 mt-1">Gérez vos colis, clients et tarifs</p>
            </div>
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Customer Lookup Section - PROMINENT */}
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white mb-8" data-testid="customer-lookup-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-400" />
                Rechercher un client par numéro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 flex gap-2">
                  <Input
                    placeholder="Entrez le numéro client (ex: CB-XXXXXX)"
                    value={customerLookup}
                    onChange={(e) => setCustomerLookup(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && handleCustomerLookup()}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                    data-testid="customer-lookup-input"
                  />
                  <Button 
                    onClick={handleCustomerLookup}
                    className="bg-blue-600 hover:bg-blue-700 h-12 px-6"
                    disabled={lookupLoading}
                    data-testid="customer-lookup-btn"
                  >
                    {lookupLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {foundCustomer && (
                  <Button 
                    variant="ghost" 
                    onClick={() => setFoundCustomer(null)}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Fermer
                  </Button>
                )}
              </div>

              {/* Customer Info Display */}
              {foundCustomer && (
                <div className="mt-6 bg-white rounded-xl p-6 text-slate-900" data-testid="customer-info-card">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                    {/* Main Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                          <User className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-xl">
                            {foundCustomer.first_name} {foundCustomer.last_name}
                          </h3>
                          <p className="text-blue-600 font-mono font-bold">{foundCustomer.customer_number}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Contact */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <Mail className="w-5 h-5 text-slate-400" />
                            <div className="flex-1">
                              <p className="text-xs text-slate-500">Email</p>
                              <p className="font-medium">{foundCustomer.email}</p>
                            </div>
                            <button onClick={() => copyToClipboard(foundCustomer.email)} className="p-1 hover:bg-slate-200 rounded">
                              <Copy className="w-4 h-4 text-slate-400" />
                            </button>
                          </div>
                          
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <Phone className="w-5 h-5 text-slate-400" />
                            <div className="flex-1">
                              <p className="text-xs text-slate-500">Téléphone</p>
                              <p className="font-medium">{foundCustomer.phone || 'Non renseigné'}</p>
                            </div>
                            {foundCustomer.phone && (
                              <button onClick={() => copyToClipboard(foundCustomer.phone)} className="p-1 hover:bg-slate-200 rounded">
                                <Copy className="w-4 h-4 text-slate-400" />
                              </button>
                            )}
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <Globe className="w-5 h-5 text-slate-400" />
                            <div className="flex-1">
                              <p className="text-xs text-slate-500">Pays de destination</p>
                              <p className="font-medium">{countryNames[foundCustomer.country] || foundCustomer.country || 'Non renseigné'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-3">
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-xs text-blue-700 font-medium mb-1">ADRESSE DE LIVRAISON</p>
                                <p className="font-medium text-slate-900">
                                  {foundCustomer.full_address || 'Adresse non renseignée'}
                                </p>
                                {foundCustomer.country && (
                                  <p className="text-slate-600 mt-1">{countryNames[foundCustomer.country] || foundCustomer.country}</p>
                                )}
                              </div>
                              {foundCustomer.full_address && (
                                <button 
                                  onClick={() => copyToClipboard(`${foundCustomer.first_name} ${foundCustomer.last_name}\n${foundCustomer.full_address}\n${countryNames[foundCustomer.country] || foundCustomer.country}`)} 
                                  className="p-1 hover:bg-blue-100 rounded"
                                >
                                  <Copy className="w-4 h-4 text-blue-600" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500">Inscrit le</p>
                            <p className="font-medium">{formatDate(foundCustomer.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex flex-col gap-2">
                      <AddPackageDialog 
                        customers={customers} 
                        onSuccess={fetchData} 
                        preselectedCustomerId={foundCustomer.id}
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => copyToClipboard(`${foundCustomer.first_name} ${foundCustomer.last_name}\n${foundCustomer.phone || ''}\n${foundCustomer.full_address || ''}\n${countryNames[foundCustomer.country] || foundCustomer.country || ''}`)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copier fiche
                      </Button>
                    </div>
                  </div>

                  {/* Customer's Packages */}
                  {(() => {
                    const customerPackages = packages.filter(p => p.customer_number === foundCustomer.customer_number);
                    if (customerPackages.length > 0) {
                      return (
                        <div className="mt-6 pt-6 border-t border-slate-200">
                          <h4 className="font-heading font-bold text-lg mb-3">Colis de ce client ({customerPackages.length})</h4>
                          <div className="space-y-2">
                            {customerPackages.map(pkg => (
                              <div key={pkg.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <Package className="w-5 h-5 text-slate-400" />
                                  <div>
                                    <p className="font-medium">{pkg.description}</p>
                                    <p className="text-sm text-slate-500">{pkg.weight} kg • {countryNames[pkg.destination_country] || pkg.destination_country}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge className={packageStatusConfig[pkg.status]?.color}>
                                    {packageStatusConfig[pkg.status]?.label}
                                  </Badge>
                                  {pkg.paid ? (
                                    <Badge className="bg-lime-100 text-lime-800">Payé</Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-800">Non payé</Badge>
                                  )}
                                  <span className="font-bold">{formatCurrency(pkg.shipping_cost)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard 
              icon={Users} 
              label="Clients" 
              value={stats?.total_customers || 0} 
              color="bg-blue-100 text-blue-600"
            />
            <StatCard 
              icon={Package} 
              label="Total colis" 
              value={stats?.total_packages || 0} 
              color="bg-slate-100 text-slate-600"
            />
            <StatCard 
              icon={RefreshCw} 
              label="En attente" 
              value={stats?.pending_packages || 0} 
              color="bg-yellow-100 text-yellow-600"
            />
            <StatCard 
              icon={DollarSign} 
              label="Payés" 
              value={stats?.paid_packages || 0} 
              color="bg-lime-100 text-lime-600"
            />
            <StatCard 
              icon={Truck} 
              label="Expédiés" 
              value={stats?.shipped_packages || 0} 
              color="bg-purple-100 text-purple-600"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Revenus" 
              value={formatCurrency(stats?.total_revenue || 0)} 
              color="bg-blue-100 text-blue-700"
              isRevenue
            />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="packages" className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="packages" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Colis
              </TabsTrigger>
              <TabsTrigger value="customers" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Clients
              </TabsTrigger>
              <TabsTrigger value="rates" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Tarifs
              </TabsTrigger>
              <TabsTrigger value="pro-quotes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Building2 className="w-4 h-4 mr-1" />
                Demandes Pro
                {proQuotes.length > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white text-xs">{proQuotes.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="fret-quotes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Ship className="w-4 h-4 mr-1" />
                Demandes Fret
                {fretQuotes.filter(q => q.status === 'new').length > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white text-xs">
                    {fretQuotes.filter(q => q.status === 'new').length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="marketing" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-marketing">
                <Megaphone className="w-4 h-4 mr-1" />
                Marketing
              </TabsTrigger>
              <TabsTrigger value="groupage" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-groupage">
                <Layers className="w-4 h-4 mr-1" />
                Groupages
                {adminGroups.filter(g => g.status === 'submitted').length > 0 && (
                  <Badge className="ml-2 bg-blue-500 text-white text-xs">
                    {adminGroups.filter(g => g.status === 'submitted').length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="storage" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-storage">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Stockage
                {((storageOverview.summary?.critical || 0) + (storageOverview.summary?.fees || 0)) > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white text-xs">
                    {(storageOverview.summary?.critical || 0) + (storageOverview.summary?.fees || 0)}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="subscribers" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-subscribers">
                <CreditCard className="w-4 h-4 mr-1" />
                Abonnés
              </TabsTrigger>
              <TabsTrigger value="deals" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900" data-testid="admin-tab-deals">
                🔥 ISM Deals
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="admin-tab-analytics">
                📊 Analytics
              </TabsTrigger>
            </TabsList>

            {/* Packages Tab */}
            <TabsContent value="packages">
              {/* Manual grouping action bar */}
              {selectedPkgIds.length > 0 && (
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg p-4 mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-lg" data-testid="grp-action-bar">
                  <div>
                    <p className="font-bold">{selectedPkgIds.length} colis sélectionné{selectedPkgIds.length > 1 ? 's' : ''}</p>
                    <p className="text-sm text-blue-100">
                      {(() => {
                        const selected = packages.filter(p => selectedPkgIds.includes(p.id));
                        const customers = [...new Set(selected.map(p => p.customer_number))];
                        const totalWeight = selected.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
                        return `Client(s): ${customers.join(', ')} · Poids total: ${totalWeight.toFixed(2)}kg`;
                      })()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        const selected = packages.filter(p => selectedPkgIds.includes(p.id));
                        const customers = [...new Set(selected.map(p => p.customer_number))];
                        if (customers.length > 1) {
                          toast.error('Les colis doivent appartenir au même client');
                          return;
                        }
                        if (selectedPkgIds.length < 2) {
                          toast.error('Sélectionnez au moins 2 colis');
                          return;
                        }
                        setGrouperName(`Groupage ${customers[0]} - ${new Date().toLocaleDateString('fr-FR')}`);
                        setGrouperPrice('');
                        setGrouperNote('');
                        setGrouperOpen(true);
                      }}
                      className="bg-white text-blue-700 hover:bg-blue-50 font-bold"
                      data-testid="grp-create-btn"
                    >
                      <Layers className="w-4 h-4 mr-2" /> Grouper ces colis
                    </Button>
                    <Button variant="outline" onClick={() => setSelectedPkgIds([])} className="bg-transparent text-white border-white hover:bg-white/10">
                      Annuler
                    </Button>
                  </div>
                </div>
              )}
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="font-heading">Gestion des colis</CardTitle>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Rechercher client..."
                          value={searchCustomer}
                          onChange={(e) => setSearchCustomer(e.target.value)}
                          className="pl-9 w-full sm:w-48"
                        />
                      </div>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-full sm:w-40">
                          <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          <SelectItem value="received">Reçu</SelectItem>
                          <SelectItem value="processing">En préparation</SelectItem>
                          <SelectItem value="shipped">Expédié</SelectItem>
                          <SelectItem value="delivered">Livré</SelectItem>
                        </SelectContent>
                      </Select>
                      <AddPackageDialog customers={customers} onSuccess={fetchData} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Poids</TableHead>
                          <TableHead>Destination</TableHead>
                          <TableHead>Transporteur</TableHead>
                          <TableHead>Prix</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Douane</TableHead>
                          <TableHead>Payé</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                              Aucun colis trouvé
                            </TableCell>
                          </TableRow>
                        ) : (
                          displayItems.map(item => {
                            if (item.type === 'group') {
                              const g = item.group;
                              const gPkgs = item.pkgs;
                              const cfg = groupStatusConfig[g.status] || groupStatusConfig.approved;
                              const firstPkg = gPkgs[0];
                              const allDescriptions = gPkgs.map(p => p.description).filter(Boolean).join(' + ');
                              const totalWeight = gPkgs.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
                              return (
                                <TableRow key={`group-${g.id}`} className="bg-purple-50/40 hover:bg-purple-50 border-l-4 border-l-purple-500" data-testid={`group-row-${g.id}`}>
                                  <TableCell>
                                    <Layers className="w-5 h-5 text-purple-600" />
                                  </TableCell>
                                  <TableCell>
                                    <button 
                                      onClick={() => {
                                        if (firstPkg) {
                                          setCustomerLookup(firstPkg.customer_number);
                                          handleCustomerLookupDirect(firstPkg.customer_number);
                                        }
                                      }}
                                      className="text-left hover:bg-white p-1 rounded"
                                    >
                                      <p className="font-medium text-slate-900">{g.client_name || firstPkg?.customer_name}</p>
                                      <p className="text-sm text-blue-600 font-mono">{g.customer_number || firstPkg?.customer_number}</p>
                                    </button>
                                  </TableCell>
                                  <TableCell className="max-w-[260px]">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge className="bg-purple-600 text-white text-[10px] font-bold">
                                        <Layers className="w-3 h-3 mr-1" /> GROUPAGE · {gPkgs.length} colis
                                      </Badge>
                                    </div>
                                    <p className="text-sm font-semibold text-slate-900 truncate" title={g.name}>{g.name}</p>
                                    <p className="text-xs text-slate-500 truncate" title={allDescriptions}>{allDescriptions}</p>
                                  </TableCell>
                                  <TableCell className="font-medium">{totalWeight.toFixed(2)} kg</TableCell>
                                  <TableCell>{countryNames[g.destination_country] || g.destination_country}</TableCell>
                                  <TableCell>
                                    <div className="text-xs">
                                      <p className="font-medium text-purple-700">Tarif personnalisé</p>
                                      <p className="text-slate-500">Défini admin</p>
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-bold text-purple-700">{formatCurrency(g.admin_price_total || g.chosen_shipping_option?.price || 0)}</TableCell>
                                  <TableCell>
                                    <Badge className={cfg.color}>{cfg.label}</Badge>
                                    {g.tracking_number && (
                                      <p className="text-[10px] text-slate-500 font-mono mt-1">📦 {g.tracking_number}</p>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className="bg-slate-100 text-slate-600 text-xs">Groupé</Badge>
                                  </TableCell>
                                  <TableCell>
                                    {g.paid ? (
                                      <Badge className="bg-lime-100 text-lime-800">Oui</Badge>
                                    ) : (
                                      <Badge className="bg-orange-100 text-orange-800">En attente</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex flex-col gap-1.5">
                                      {!g.paid && g.status === 'approved' && (
                                        <>
                                          <Button size="sm" variant="outline" onClick={() => resendGroupPaymentEmail(g)} className="h-7 text-xs" title="Renvoyer email paiement">
                                            <Mail className="w-3.5 h-3.5 mr-1" /> Relancer
                                          </Button>
                                          <Button size="sm" variant="outline" onClick={async () => {
                                            if (!window.confirm(`Marquer "${g.name}" comme payé manuellement ?`)) return;
                                            try {
                                              await api.post(`/admin/groups/${g.id}/mark-paid`);
                                              toast.success('Groupage marqué comme payé');
                                              await fetchData();
                                            } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                                          }} className="h-7 text-xs border-emerald-500 text-emerald-700" title="Marquer manuellement comme payé">
                                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Payé manuel
                                          </Button>
                                        </>
                                      )}
                                      {['approved', 'preparing'].includes(g.status) && (
                                        <Button size="sm" onClick={() => openShipGroup(g)} className="h-7 text-xs bg-purple-600 hover:bg-purple-700" data-testid={`group-ship-${g.id}`}>
                                          <Truck className="w-3.5 h-3.5 mr-1" /> Expédier
                                        </Button>
                                      )}
                                      {g.status === 'shipped' && (
                                        <Button size="sm" onClick={() => markGroupDelivered(g)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700">
                                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Livré
                                        </Button>
                                      )}
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => { setGroupDetailTarget(g); setGroupDetailOpen(true); }} className="h-7 text-xs px-2" title="Voir détails">
                                          <Eye className="w-3.5 h-3.5" />
                                        </Button>
                                        {g.status !== 'shipped' && g.status !== 'delivered' && (
                                          <Button size="sm" variant="ghost" onClick={() => cancelGroup(g)} className="h-7 text-xs px-2 text-red-600 hover:text-red-700" title="Dégrouper">
                                            <X className="w-3.5 h-3.5" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            const pkg = item.pkg;
                            return (
                            <TableRow key={pkg.id} className={selectedPkgIds.includes(pkg.id) ? 'bg-blue-50' : ''}>
                              <TableCell>
                                {!pkg.group_id && pkg.status !== 'shipped' && pkg.status !== 'delivered' && (
                                  <input
                                    type="checkbox"
                                    checked={selectedPkgIds.includes(pkg.id)}
                                    onChange={() => setSelectedPkgIds(prev => prev.includes(pkg.id) ? prev.filter(x => x !== pkg.id) : [...prev, pkg.id])}
                                    className="w-4 h-4 cursor-pointer"
                                    data-testid={`pkg-check-${pkg.id}`}
                                  />
                                )}
                                {pkg.group_id && <Badge className="bg-purple-100 text-purple-800 text-[10px]">GROUPÉ</Badge>}
                              </TableCell>
                              <TableCell>
                                <button 
                                  onClick={() => {
                                    setCustomerLookup(pkg.customer_number);
                                    handleCustomerLookupDirect(pkg.customer_number);
                                  }}
                                  className="text-left hover:bg-slate-50 p-1 rounded"
                                >
                                  <p className="font-medium text-slate-900">{pkg.customer_name}</p>
                                  <p className="text-sm text-blue-600 font-mono">{pkg.customer_number}</p>
                                </button>
                              </TableCell>
                              <TableCell className="max-w-[200px] truncate">{pkg.description}</TableCell>
                              <TableCell>{pkg.weight} kg</TableCell>
                              <TableCell>{countryNames[pkg.destination_country] || pkg.destination_country}</TableCell>
                              <TableCell>
                                {pkg.shipping_option ? (
                                  <div className="text-xs">
                                    <p className="font-medium text-slate-900">{pkg.shipping_option.name}</p>
                                    <p className="text-slate-500">{pkg.shipping_option.carrier}</p>
                                    <p className="text-slate-400">{pkg.shipping_option.delivery_time}</p>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-xs">Non choisi</span>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{formatCurrency(pkg.shipping_cost)}</TableCell>
                              <TableCell>
                                <Badge className={packageStatusConfig[pkg.status]?.color}>
                                  {packageStatusConfig[pkg.status]?.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {pkg.customs_declaration ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                    <FileText className="w-3 h-3 mr-1" />OK
                                  </Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-500 text-xs">-</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {pkg.paid ? (
                                  <Badge className="bg-lime-100 text-lime-800">Oui</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-800">Non</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <EditPackageDialog pkg={pkg} onSuccess={fetchData} />
                                  <DeletePackageButton packageId={pkg.id} onSuccess={fetchData} />
                                </div>
                              </TableCell>
                            </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Customers Tab */}
            <TabsContent value="customers">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <CardTitle className="font-heading">Liste des clients</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Numéro client</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Téléphone</TableHead>
                          <TableHead>Adresse</TableHead>
                          <TableHead>Pays</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                              Aucun client
                            </TableCell>
                          </TableRow>
                        ) : (
                          customers.map(customer => (
                            <TableRow key={customer.id}>
                              <TableCell>
                                <button 
                                  onClick={() => {
                                    setCustomerLookup(customer.customer_number);
                                    setFoundCustomer(customer);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  className="font-mono font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  {customer.customer_number}
                                </button>
                              </TableCell>
                              <TableCell className="font-medium">
                                {customer.first_name} {customer.last_name}
                              </TableCell>
                              <TableCell>{customer.email}</TableCell>
                              <TableCell>{customer.phone || '-'}</TableCell>
                              <TableCell className="max-w-[200px] truncate">
                                {customer.full_address || '-'}
                              </TableCell>
                              <TableCell>{countryNames[customer.country] || customer.country || '-'}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                      setCustomerLookup(customer.customer_number);
                                      setFoundCustomer(customer);
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                  >
                                    <User className="w-4 h-4 mr-1" />
                                    Voir
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={async () => {
                                      if (window.confirm(`Êtes-vous sûr de vouloir supprimer le client ${customer.first_name} ${customer.last_name} (${customer.customer_number}) ?\n\nTous ses colis seront également supprimés.`)) {
                                        try {
                                          await adminCustomersAPI.delete(customer.id);
                                          toast.success('Client supprimé avec succès');
                                          fetchData();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Rates Tab */}
            <TabsContent value="rates">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading">Grille tarifaire</CardTitle>
                    <div className="flex gap-2">
                      {rates.length === 0 && (
                        <Button variant="outline" onClick={seedDefaultRates}>
                          Créer tarifs par défaut
                        </Button>
                      )}
                      <AddRateDialog onSuccess={fetchData} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Zone</TableHead>
                          <TableHead>Pays</TableHead>
                          <TableHead>Prix de base</TableHead>
                          <TableHead>Prix/kg</TableHead>
                          <TableHead>Poids min</TableHead>
                          <TableHead>Poids max</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rates.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                              Aucun tarif configuré
                            </TableCell>
                          </TableRow>
                        ) : (
                          rates.map(rate => (
                            <TableRow key={rate.id}>
                              <TableCell className="font-medium">{rate.zone_name}</TableCell>
                              <TableCell className="max-w-[200px]">
                                <div className="flex flex-wrap gap-1">
                                  {rate.countries.slice(0, 3).map(c => (
                                    <Badge key={c} variant="outline" className="text-xs">
                                      {c}
                                    </Badge>
                                  ))}
                                  {rate.countries.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{rate.countries.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{formatCurrency(rate.base_price)}</TableCell>
                              <TableCell>{formatCurrency(rate.price_per_kg)}</TableCell>
                              <TableCell>{rate.min_weight} kg</TableCell>
                              <TableCell>{rate.max_weight} kg</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <EditRateDialog rate={rate} onSuccess={fetchData} />
                                  <DeleteRateButton rateId={rate.id} onSuccess={fetchData} />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pro Quotes Tab */}
            <TabsContent value="pro-quotes">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-6 h-6 text-blue-600" />
                      <div>
                        <CardTitle className="font-heading">Demandes Pro</CardTitle>
                        <p className="text-sm text-slate-500">Demandes de devis professionnels</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-lg px-4 py-1">
                      {proQuotes.length} demande{proQuotes.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {proQuotes.length === 0 ? (
                    <div className="text-center py-12">
                      <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Aucune demande Pro pour le moment</p>
                      <p className="text-sm text-slate-400 mt-1">Les demandes du formulaire /pro apparaîtront ici</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {proQuotes.map((quote) => (
                        <div key={quote.id} className="p-6 hover:bg-slate-50 transition-colors">
                          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                            {/* Company & Contact Info */}
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-heading font-bold text-lg text-slate-900">
                                  {quote.company}
                                </h3>
                                <Badge className="bg-blue-100 text-blue-700">
                                  {quote.volume || 'Volume non spécifié'}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                                <div className="flex items-center gap-2 text-sm">
                                  <User className="w-4 h-4 text-slate-400" />
                                  <span className="text-slate-700">{quote.name}</span>
                                </div>
                                <a 
                                  href={`mailto:${quote.email}`}
                                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                                >
                                  <Mail className="w-4 h-4" />
                                  <span>{quote.email}</span>
                                </a>
                                <a 
                                  href={`tel:${quote.phone}`}
                                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                                >
                                  <Phone className="w-4 h-4" />
                                  <span>{quote.phone}</span>
                                </a>
                              </div>
                              
                              {/* Message */}
                              <div className="bg-slate-50 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                  <MessageSquare className="w-4 h-4" />
                                  <span>Message du client</span>
                                </div>
                                <p className="text-slate-700 whitespace-pre-wrap">{quote.message}</p>
                              </div>
                            </div>
                            
                            {/* Actions & Date */}
                            <div className="flex flex-col items-end gap-3 min-w-[200px]">
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Clock className="w-4 h-4" />
                                <span>{formatDate(quote.created_at)}</span>
                              </div>
                              
                              <div className="flex gap-2">
                                <a
                                  href={`mailto:${quote.email}?subject=Re: Votre demande de devis ISMParcel Pro`}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                  <Mail className="w-4 h-4" />
                                  Répondre
                                </a>
                                <a
                                  href={`tel:${quote.phone}`}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                                >
                                  <Phone className="w-4 h-4" />
                                  Appeler
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Fret Quotes Tab */}
            <TabsContent value="fret-quotes">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Ship className="w-6 h-6 text-amber-600" />
                      <div>
                        <CardTitle className="font-heading">Demandes Fret</CardTitle>
                        <p className="text-sm text-slate-500">Véhicules, palettes, déménagements, fret commercial</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {['all', 'new', 'quoted', 'accepted', 'in_progress', 'delivered', 'cancelled'].map(s => (
                        <button
                          key={s}
                          onClick={() => setFretFilter(s)}
                          className={`text-xs px-3 py-1.5 rounded-full border ${fretFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}
                          data-testid={`fret-filter-${s}`}
                        >
                          {s === 'all' ? 'Tous' : s === 'new' ? 'Nouveau' : s === 'quoted' ? 'Devisé' : s === 'accepted' ? 'Accepté' : s === 'in_progress' ? 'En cours' : s === 'delivered' ? 'Livré' : 'Annulé'}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {(() => {
                    const visible = fretFilter === 'all' ? fretQuotes : fretQuotes.filter(q => q.status === fretFilter);
                    if (visible.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <Ship className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500">Aucune demande fret</p>
                          <p className="text-sm text-slate-400 mt-1">Les demandes du formulaire /fret apparaîtront ici</p>
                        </div>
                      );
                    }
                    const serviceIcons = { vehicule: Car, palette: Package, demenagement: Home, grossiste: Building2 };
                    const serviceLabels = { vehicule: 'Véhicule', palette: 'Palette', demenagement: 'Déménagement', grossiste: 'Grossiste' };
                    const statusColors = { new: 'bg-amber-100 text-amber-800', quoted: 'bg-blue-100 text-blue-800', accepted: 'bg-emerald-100 text-emerald-800', in_progress: 'bg-purple-100 text-purple-800', delivered: 'bg-slate-100 text-slate-800', cancelled: 'bg-red-100 text-red-800' };
                    const statusLabels = { new: 'Nouveau', quoted: 'Devisé', accepted: 'Accepté', in_progress: 'En cours', delivered: 'Livré', cancelled: 'Annulé' };

                    const updateStatus = async (id, newStatus) => {
                      try {
                        await api.patch(`/admin/fret-quotes/${id}`, { status: newStatus });
                        setFretQuotes(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q));
                        toast.success('Statut mis à jour');
                      } catch {
                        toast.error('Erreur mise à jour');
                      }
                    };
                    const deleteQuote = async (id) => {
                      if (!window.confirm('Supprimer cette demande ?')) return;
                      try {
                        await api.delete(`/admin/fret-quotes/${id}`);
                        setFretQuotes(prev => prev.filter(q => q.id !== id));
                        toast.success('Demande supprimée');
                      } catch {
                        toast.error('Erreur suppression');
                      }
                    };

                    return (
                      <div className="divide-y divide-slate-200">
                        {visible.map((q) => {
                          const Icon = serviceIcons[q.service_type] || Package;
                          return (
                            <div key={q.id} className="p-5 hover:bg-slate-50 transition-colors" data-testid={`fret-quote-${q.id}`}>
                              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                      <Icon className="w-5 h-5 text-amber-700" />
                                    </div>
                                    <h3 className="font-heading font-bold text-lg text-slate-900">
                                      {q.name}
                                      {q.is_company && q.company && <span className="text-slate-500 font-normal"> — {q.company}</span>}
                                    </h3>
                                    <Badge className={statusColors[q.status] || 'bg-slate-100'}>
                                      {statusLabels[q.status] || q.status}
                                    </Badge>
                                    <Badge variant="outline">{serviceLabels[q.service_type]}</Badge>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm mb-3">
                                    <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 text-blue-600 hover:underline"><Mail className="w-3.5 h-3.5" />{q.email}</a>
                                    <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 text-blue-600 hover:underline"><Phone className="w-3.5 h-3.5" />{q.phone}</a>
                                    <div className="flex items-center gap-1.5 text-slate-600"><MapPin className="w-3.5 h-3.5" />{q.pickup_city} → {q.destination_country}</div>
                                    <div className="flex items-center gap-1.5 text-slate-500"><Clock className="w-3.5 h-3.5" />{formatDate(q.created_at)}</div>
                                  </div>

                                  {/* Pickup & Delivery methods */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                                    {q.pickup_method && (
                                      <div className="bg-blue-50 border border-blue-100 rounded px-3 py-2 text-xs">
                                        <strong className="text-blue-900">Enlèvement :</strong>{' '}
                                        {q.pickup_method === 'enlevement_domicile' && '🚚 À domicile'}
                                        {q.pickup_method === 'depot_entrepot' && '🏭 Dépôt entrepôt'}
                                        {q.pickup_method === 'livraison_fournisseur' && '📦 Livré par fournisseur'}
                                        {q.pickup_address && <div className="text-slate-600 mt-0.5">{q.pickup_address}</div>}
                                        {q.pickup_contact_name && <div className="text-slate-600">Contact : {q.pickup_contact_name} {q.pickup_contact_phone && `· ${q.pickup_contact_phone}`}</div>}
                                      </div>
                                    )}
                                    {q.delivery_method && (
                                      <div className="bg-emerald-50 border border-emerald-100 rounded px-3 py-2 text-xs">
                                        <strong className="text-emerald-900">Livraison :</strong>{' '}
                                        {q.delivery_method === 'port_terminal' && '🚢 Au port'}
                                        {q.delivery_method === 'domicile_dest' && '🏠 À domicile'}
                                        {q.delivery_method === 'entrepot_dest' && '🏭 Entrepôt'}
                                        {q.delivery_method === 'retrait_agence' && '🏪 Retrait agence'}
                                        {q.destination_address && <div className="text-slate-600 mt-0.5">{q.destination_address}</div>}
                                        {q.recipient_name && <div className="text-slate-600">Destinataire : {q.recipient_name} {q.recipient_phone && `· ${q.recipient_phone}`}</div>}
                                      </div>
                                    )}
                                  </div>

                                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 mb-2">
                                    <strong>Description : </strong>{q.description}
                                  </div>

                                  {/* Specific */}
                                  {q.service_type === 'vehicule' && (
                                    <div className="text-sm text-slate-600 mb-2">
                                      <strong>Véhicule :</strong> {[q.vehicle_brand, q.vehicle_model, q.vehicle_year].filter(Boolean).join(' ')} {q.vehicle_value ? `— ${q.vehicle_value}€` : ''} {q.vehicle_running ? '(roulant)' : '(non roulant)'}
                                    </div>
                                  )}
                                  {(q.service_type === 'palette' || q.service_type === 'grossiste') && (
                                    <div className="text-sm text-slate-600 mb-2">
                                      <strong>Marchandise :</strong> {q.nb_pallets ? `${q.nb_pallets} pal. · ` : ''}{q.total_weight ? `${q.total_weight} kg · ` : ''}{q.total_volume ? `${q.total_volume} m³` : ''}
                                    </div>
                                  )}
                                  {q.service_type === 'demenagement' && (
                                    <div className="text-sm text-slate-600 mb-2">
                                      <strong>Déménagement :</strong> {q.container_size} {q.volume_m3 ? `· ${q.volume_m3} m³` : ''} {q.needs_packing ? '· emballage inclus' : ''}
                                    </div>
                                  )}

                                  {(q.declared_value || q.already_packed || q.needs_packaging || q.has_customs_docs || q.frequency) && (
                                    <div className="flex flex-wrap gap-2 mb-2 text-xs">
                                      {q.declared_value && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Valeur : {q.declared_value}€</span>}
                                      {q.already_packed && <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">Déjà emballé</span>}
                                      {q.needs_packaging && <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">Emballage demandé</span>}
                                      {q.has_customs_docs && <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">📄 Docs douane OK</span>}
                                      {q.frequency && q.frequency !== 'ponctuel' && <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded">🔁 {q.frequency.replace('recurrent_', 'Récurrent ')}</span>}
                                    </div>
                                  )}

                                  {q.photos && q.photos.length > 0 && (
                                    <button
                                      onClick={() => setSelectedFretQuote(q)}
                                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                                      data-testid={`fret-photos-${q.id}`}
                                    >
                                      <ImageIcon className="w-4 h-4" /> {q.photos.length} photo{q.photos.length > 1 ? 's' : ''}
                                    </button>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col gap-2 min-w-[170px]">
                                  <select
                                    value={q.status}
                                    onChange={e => updateStatus(q.id, e.target.value)}
                                    className="text-sm border border-slate-300 rounded-md px-2 py-1.5"
                                    data-testid={`fret-status-${q.id}`}
                                  >
                                    {Object.entries(statusLabels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                  </select>
                                  <a
                                    href={`mailto:${q.email}?subject=Re: Votre demande de devis fret ISMParcel`}
                                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                                  >
                                    <Mail className="w-4 h-4" /> Répondre
                                  </a>
                                  <button
                                    onClick={() => deleteQuote(q.id)}
                                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-md hover:bg-red-100 text-sm"
                                    data-testid={`fret-delete-${q.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" /> Supprimer
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Photos modal */}
              {selectedFretQuote && (
                <div
                  className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
                  onClick={() => setSelectedFretQuote(null)}
                >
                  <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-lg">Photos — {selectedFretQuote.name}</h3>
                      <button onClick={() => setSelectedFretQuote(null)} className="p-2 hover:bg-slate-100 rounded">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedFretQuote.photos.map((p, i) => (
                        <a key={i} href={p.data} target="_blank" rel="noreferrer" className="block">
                          <img src={p.data} alt={p.filename} className="w-full h-48 object-cover rounded-lg border hover:opacity-90" />
                          <p className="text-xs text-slate-500 mt-1 truncate">{p.filename}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Marketing Tab */}
            <TabsContent value="marketing">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <Megaphone className="w-6 h-6 text-purple-600" />
                    <div>
                      <CardTitle className="font-heading">Campagnes marketing</CardTitle>
                      <p className="text-sm text-slate-500">Envoyez une newsletter mensuelle à vos clients pour les relancer</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {(() => {
                    const loadMarketing = async () => {
                      try {
                        const [aud, camps] = await Promise.all([
                          api.get('/admin/marketing/audience'),
                          api.get('/admin/marketing/campaigns'),
                        ]);
                        setMkAudience(aud.data);
                        setMkCampaigns(camps.data || []);
                      } catch (e) {
                        // silent — first load handled by button
                      }
                    };
                    const loadPreview = async () => {
                      try {
                        const res = await api.get('/admin/marketing/preview');
                        setMkPreviewHtml(res.data.html);
                      } catch {
                        toast.error('Erreur chargement aperçu');
                      }
                    };
                    const sendCampaign = async (testOnly = false) => {
                      const label = testOnly ? 'Envoyer un test à votre email admin ?' : `Envoyer la newsletter à ${mkAudience[mkTarget]} client(s) ?\n\nCette action est IRREVERSIBLE.`;
                      if (!window.confirm(label)) return;
                      setMkSending(true);
                      try {
                        const res = await api.post('/admin/marketing/newsletter', { target: mkTarget, test_only: testOnly });
                        toast.success(res.data.message || 'Envoi lancé');
                        await loadMarketing();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Erreur envoi');
                      } finally {
                        setMkSending(false);
                      }
                    };
                    // Trigger load on mount of this tab content
                    if (!mkPreviewHtml && mkCampaigns.length === 0) {
                      loadMarketing();
                      loadPreview();
                    }
                    return (
                      <>
                        {/* Audience selector */}
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4" /> Choisir l'audience
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {[
                              { id: 'all', label: 'Tous les clients', desc: 'Newsletter à toute la base', color: 'from-blue-500 to-cyan-500' },
                              { id: 'active_only', label: 'Clients actifs', desc: 'Ayant commandé sous 60 jours', color: 'from-emerald-500 to-teal-500' },
                              { id: 'inactive_30d', label: 'Inactifs +30j', desc: 'Pas de colis depuis 30 jours', color: 'from-amber-500 to-orange-500' },
                              { id: 'inactive_60d', label: 'Inactifs +60j', desc: 'Dormant — fort potentiel relance', color: 'from-red-500 to-pink-500' },
                            ].map(opt => {
                              const active = mkTarget === opt.id;
                              const count = mkAudience[opt.id] || 0;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => setMkTarget(opt.id)}
                                  data-testid={`mk-target-${opt.id}`}
                                  className={`text-left p-4 rounded-xl border-2 transition-all ${active ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'}`}
                                >
                                  <div className={`text-3xl font-bold bg-gradient-to-br ${opt.color} bg-clip-text text-transparent mb-1`}>{count}</div>
                                  <h5 className="font-semibold text-slate-900 text-sm">{opt.label}</h5>
                                  <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-3 pt-2">
                          <Button
                            onClick={() => sendCampaign(false)}
                            disabled={mkSending || (mkAudience[mkTarget] || 0) === 0}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                            data-testid="mk-send-campaign"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {mkSending ? 'Envoi...' : `Envoyer à ${mkAudience[mkTarget] || 0} client(s)`}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => sendCampaign(true)}
                            disabled={mkSending}
                            data-testid="mk-send-test"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Envoi test (moi uniquement)
                          </Button>
                          <Button variant="outline" onClick={loadPreview} data-testid="mk-reload-preview">
                            <Eye className="w-4 h-4 mr-2" />
                            Rafraîchir aperçu
                          </Button>
                        </div>

                        {/* Preview */}
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Eye className="w-4 h-4" /> Aperçu de l'email
                          </h4>
                          <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-100 p-2">
                            {mkPreviewHtml ? (
                              <iframe
                                title="newsletter-preview"
                                srcDoc={mkPreviewHtml}
                                className="w-full h-[500px] bg-white rounded"
                                data-testid="mk-preview-iframe"
                              />
                            ) : (
                              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
                                Chargement de l'aperçu...
                              </div>
                            )}
                          </div>
                        </div>

                        {/* History */}
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-3">Historique des campagnes</h4>
                          {mkCampaigns.length === 0 ? (
                            <p className="text-sm text-slate-500 py-6 text-center">Aucune campagne envoyée pour le moment.</p>
                          ) : (
                            <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                              {mkCampaigns.map((c) => (
                                <div key={c.id} className="p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50">
                                  <div>
                                    <p className="font-medium text-slate-900 text-sm">
                                      {c.test_only ? '🧪 Test' : '📧 Newsletter'} · cible : {c.target}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {formatDate(c.created_at)} · envoyé par {c.sent_by}
                                    </p>
                                  </div>
                                  <div className="flex gap-2 text-sm">
                                    <Badge className={c.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                      {c.status === 'completed' ? 'Envoyé' : 'En cours'}
                                    </Badge>
                                    <span className="text-slate-600">
                                      ✅ {c.sent_count || 0} / {c.recipients_count}
                                      {c.failed_count > 0 && <span className="text-red-600 ml-2">❌ {c.failed_count}</span>}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                          💡 <strong>Astuce :</strong> Envoyez cette newsletter le 1er ou 2ème jour de chaque mois pour maximiser l'engagement. Les campagnes ciblées sur les <strong>inactifs +30j</strong> convertissent généralement mieux que les envois massifs.
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Groupage Tab */}
            <TabsContent value="groupage">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Layers className="w-6 h-6 text-blue-600" />
                      <div>
                        <CardTitle className="font-heading">Groupages clients</CardTitle>
                        <p className="text-sm text-slate-500">Regroupements de colis demandés par vos clients</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['all', 'submitted', 'approved', 'preparing', 'shipped', 'delivered', 'cancelled'].map(s => (
                        <button
                          key={s}
                          onClick={() => setGroupFilter(s)}
                          className={`text-xs px-3 py-1.5 rounded-full border ${groupFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}
                        >
                          {s === 'all' ? 'Tous' : s === 'submitted' ? 'Soumis' : s === 'approved' ? 'Approuvé' : s === 'preparing' ? 'Préparation' : s === 'shipped' ? 'Expédié' : s === 'delivered' ? 'Livré' : 'Annulé'}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {(() => {
                    const visible = groupFilter === 'all' ? adminGroups : adminGroups.filter(g => g.status === groupFilter);
                    const labels = { draft: 'Brouillon', submitted: 'Soumis', approved: 'Approuvé', preparing: 'Préparation', shipped: 'Expédié', delivered: 'Livré', cancelled: 'Annulé' };
                    const colors = { draft: 'bg-slate-100 text-slate-800', submitted: 'bg-blue-100 text-blue-800', approved: 'bg-emerald-100 text-emerald-800', preparing: 'bg-amber-100 text-amber-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-slate-100', cancelled: 'bg-red-100 text-red-800' };

                    const updateStatus = async (id, status, extra = {}) => {
                      try {
                        await api.patch(`/admin/groups/${id}/status`, { status, ...extra });
                        toast.success(`Statut → ${labels[status] || status}`);
                        const res = await api.get('/admin/groups');
                        setAdminGroups(res.data || []);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Erreur');
                      }
                    };

                    if (visible.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500">Aucun groupage</p>
                        </div>
                      );
                    }
                    return (
                      <div className="divide-y divide-slate-200">
                        {visible.map(g => (
                          <div key={g.id} className="p-5" data-testid={`admin-group-${g.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <h4 className="font-heading font-bold text-slate-900">{g.name}</h4>
                                  <Badge className={colors[g.status] || 'bg-slate-100'}>{labels[g.status] || g.status}</Badge>
                                </div>
                                <p className="text-sm text-slate-600">
                                  <strong>{g.client_name}</strong> · {g.customer_number} · {g.client_email}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {g.packages_count} colis · {g.total_weight}kg · vers {g.destination_country} · créé {formatDate(g.created_at)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-500">Économie client</p>
                                <p className="font-heading font-bold text-xl text-emerald-600">{g.savings}€</p>
                              </div>
                            </div>

                            {g.chosen_shipping_option && (
                              <div className="bg-slate-50 rounded p-2 text-xs mb-2">
                                <strong>Transport:</strong> {g.chosen_shipping_option.name} ({g.chosen_shipping_option.carrier}) — <strong>{g.chosen_shipping_option.price}€</strong>
                                {g.chosen_shipping_option.management_fee !== undefined && ` + ${g.chosen_shipping_option.management_fee}€ frais`}
                                {g.admin_price_override && (
                                  <Badge className="ml-2 bg-purple-100 text-purple-800 text-[10px]">PRIX ADMIN AJUSTÉ</Badge>
                                )}
                                {g.admin_price_note && (
                                  <p className="text-slate-500 italic mt-1">Note : {g.admin_price_note}</p>
                                )}
                              </div>
                            )}

                            {g.customs_declaration ? (
                              <details className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs mb-2">
                                <summary className="cursor-pointer font-semibold text-emerald-900">
                                  📄 Déclaration douanière ({g.customs_declaration.items?.length || 0} articles · {g.customs_declaration.total_value}€ · {g.customs_declaration.purpose})
                                </summary>
                                <div className="mt-2 space-y-1">
                                  {(g.customs_declaration.items || []).map((it, i) => (
                                    <div key={i} className="flex justify-between text-slate-700">
                                      <span>{it.quantity}× {it.description} ({it.country_of_origin})</span>
                                      <span className="font-medium">{(it.value * it.quantity).toFixed(2)}€ · {(it.weight * it.quantity).toFixed(2)}kg</span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            ) : (
                              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs mb-2 text-red-700">
                                ⚠️ <strong>Aucune déclaration douanière</strong> — le client doit la remplir avant expédition.
                              </div>
                            )}

                            {g.packages_preview && g.packages_preview.length > 0 && (
                              <div className="text-xs text-slate-600 mb-3">
                                <strong>Colis :</strong> {g.packages_preview.map(p => p.description).join(' · ')}
                                {g.packages_count > 3 && ` ... +${g.packages_count - 3}`}
                              </div>
                            )}

                            <div className="flex flex-wrap gap-2">
                              {g.status === 'submitted' && (
                                <Button size="sm" onClick={() => updateStatus(g.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700">
                                  ✅ Approuver
                                </Button>
                              )}
                              {!['shipped', 'delivered', 'cancelled'].includes(g.status) && g.chosen_shipping_option && (
                                <Button size="sm" variant="outline" onClick={async () => {
                                  const carrier = parseFloat(window.prompt(`Prix transporteur (€)\n\nPrix actuel : ${g.chosen_shipping_option?.price || 0}€`, g.chosen_shipping_option?.price || 0));
                                  if (isNaN(carrier) || carrier < 0) return;
                                  const fees = parseFloat(window.prompt(`Frais de gestion (€)\n\nActuel : ${g.chosen_shipping_option?.management_fee ?? 15}€`, g.chosen_shipping_option?.management_fee ?? 15));
                                  if (isNaN(fees) || fees < 0) return;
                                  const note = window.prompt('Note (optionnel — ex: ajustement transporteur, surcharge carburant, etc.)', g.admin_price_note || '') || '';
                                  try {
                                    const res = await api.patch(`/admin/groups/${g.id}/price`, { carrier_price: carrier, management_fee: fees, admin_note: note });
                                    toast.success(`Prix mis à jour : ${res.data.total_price}€`);
                                    const refreshed = await api.get('/admin/groups');
                                    setAdminGroups(refreshed.data || []);
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || 'Erreur mise à jour prix');
                                  }
                                }} className="bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100">
                                  💰 Modifier le prix
                                </Button>
                              )}
                              {g.status === 'approved' && (
                                <Button size="sm" onClick={() => updateStatus(g.id, 'preparing')}>
                                  📦 Préparer
                                </Button>
                              )}
                              {g.status === 'preparing' && (
                                <Button size="sm" onClick={() => {
                                  const tracking = window.prompt('Numéro de tracking ?');
                                  if (tracking) updateStatus(g.id, 'shipped', { tracking_number: tracking });
                                }} className="bg-purple-600 hover:bg-purple-700">
                                  🚢 Expédier
                                </Button>
                              )}
                              {g.status === 'shipped' && (
                                <Button size="sm" onClick={() => updateStatus(g.id, 'delivered')} variant="outline">
                                  ✓ Marquer livré
                                </Button>
                              )}
                              {!['shipped', 'delivered', 'cancelled'].includes(g.status) && (
                                <Button size="sm" onClick={() => {
                                  if (window.confirm('Annuler ce groupage ?')) updateStatus(g.id, 'cancelled');
                                }} variant="outline" className="text-red-600">
                                  Annuler
                                </Button>
                              )}
                              <a href={`mailto:${g.client_email}?subject=Votre groupage ISMParcel`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded text-xs">
                                <Mail className="w-3.5 h-3.5" /> Email
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Storage Tab */}
            <TabsContent value="storage">
              <Card className="bg-white border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                    <div>
                      <CardTitle className="font-heading">Stockage entrepôt</CardTitle>
                      <p className="text-sm text-slate-500">Surveillez les colis en stockage long (30j gratuit, puis 0,50€/jour, limite 60j)</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{storageOverview.summary?.total || 0}</p>
                      <p className="text-xs text-slate-500">Total non expédiés</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-700">{storageOverview.summary?.ok || 0}</p>
                      <p className="text-xs text-slate-500">OK (&lt;25j)</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-700">{storageOverview.summary?.warning || 0}</p>
                      <p className="text-xs text-slate-500">Alerte (25-29j)</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-orange-700">{storageOverview.summary?.fees || 0}</p>
                      <p className="text-xs text-slate-500">Frais en cours</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{storageOverview.summary?.critical || 0}</p>
                      <p className="text-xs text-slate-500">Critique (&gt;60j)</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-900">
                    💰 <strong>Total frais de stockage à facturer :</strong> {storageOverview.summary?.total_fees_owed || 0}€
                  </div>

                  {/* Packages list */}
                  {storageOverview.packages?.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Aucun colis en stockage</p>
                  ) : (
                    <div className="space-y-2">
                      {(storageOverview.packages || []).map(p => {
                        const colors = { ok: 'bg-emerald-50 border-emerald-200', warning: 'bg-amber-50 border-amber-200', fees: 'bg-orange-50 border-orange-200', critical: 'bg-red-50 border-red-200' };
                        const labels = { ok: 'OK', warning: 'Alerte', fees: 'Frais', critical: 'CRITIQUE' };
                        return (
                          <div key={p.id} className={`border-2 rounded-lg p-3 ${colors[p.storage_status] || ''}`} data-testid={`storage-pkg-${p.id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-slate-900 truncate">{p.description}</p>
                                  <Badge className={p.storage_status === 'critical' ? 'bg-red-600 text-white' : p.storage_status === 'fees' ? 'bg-orange-500 text-white' : p.storage_status === 'warning' ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'}>
                                    {labels[p.storage_status]}
                                  </Badge>
                                  <span className="text-xs text-slate-500">{p.customer_number} · {p.customer_name}</span>
                                </div>
                                <p className="text-xs text-slate-600 mt-1">
                                  <strong>{p.storage_days} jours</strong> de stockage · {p.weight}kg · vers {p.destination_country}
                                  {p.storage_fee > 0 && <span className="ml-2 font-bold text-orange-700">Frais : {p.storage_fee}€</span>}
                                </p>
                              </div>
                              {p.storage_status === 'critical' && (
                                <span className="text-xs text-red-700 font-bold">⚠️ ACTION REQUISE</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Subscribers Tab */}
            <TabsContent value="subscribers">
              <Card>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-emerald-600" />
                        Abonnés & Non-abonnés
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">Identifiez les clients abonnés pour appliquer les tarifs adaptés.</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                        <div className="text-xs text-emerald-700 font-semibold uppercase">Abonnés</div>
                        <div className="text-2xl font-black text-emerald-800">{subscribers.filter(c => c.subscription?.is_subscribed).length}</div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <div className="text-xs text-amber-700 font-semibold uppercase">Starter</div>
                        <div className="text-2xl font-black text-amber-800">{subscribers.filter(c => c.subscription?.plan_id === 'starter').length}</div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <div className="text-xs text-blue-700 font-semibold uppercase">Pro</div>
                        <div className="text-2xl font-black text-blue-800">{subscribers.filter(c => c.subscription?.plan_id === 'pro').length}</div>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                        <div className="text-xs text-purple-700 font-semibold uppercase">Business</div>
                        <div className="text-2xl font-black text-purple-800">{subscribers.filter(c => c.subscription?.plan_id === 'business').length}</div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 flex gap-2 flex-wrap">
                    <Input
                      placeholder="Rechercher (nom, email, n° client)..."
                      value={searchCustomer}
                      onChange={e => setSearchCustomer(e.target.value)}
                      className="max-w-xs"
                      data-testid="sub-search"
                    />
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Statut</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>N° client</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Formule</TableHead>
                          <TableHead>Quota utilisé</TableHead>
                          <TableHead>Prochain renouv.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscribers
                          .filter(c => {
                            if (!searchCustomer) return true;
                            const q = searchCustomer.toLowerCase();
                            return (c.first_name || '').toLowerCase().includes(q)
                              || (c.last_name || '').toLowerCase().includes(q)
                              || (c.email || '').toLowerCase().includes(q)
                              || (c.customer_number || '').toLowerCase().includes(q);
                          })
                          .sort((a, b) => Number(b.subscription?.is_subscribed) - Number(a.subscription?.is_subscribed))
                          .map(c => {
                            const s = c.subscription || {};
                            return (
                              <TableRow key={c.id} className={s.is_subscribed ? 'bg-emerald-50/50' : ''} data-testid={`sub-row-${c.id}`}>
                                <TableCell>
                                  {s.is_subscribed ? (
                                    <Badge className="bg-emerald-500 text-white">✓ Abonné</Badge>
                                  ) : (
                                    <Badge className="bg-slate-200 text-slate-700">Non abonné</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{c.first_name} {c.last_name}</TableCell>
                                <TableCell className="font-mono text-blue-600">{c.customer_number}</TableCell>
                                <TableCell className="text-sm">{c.email}</TableCell>
                                <TableCell>
                                  {s.plan_name ? (
                                    <Badge className={
                                      s.plan_id === 'business' ? 'bg-purple-600 text-white' :
                                      s.plan_id === 'pro' ? 'bg-blue-600 text-white' :
                                      'bg-amber-500 text-white'
                                    }>
                                      {s.plan_name}
                                    </Badge>
                                  ) : (
                                    <span className="text-slate-400 text-sm">—</span>
                                  )}
                                  {s.cancel_at_period_end && (
                                    <span className="ml-2 text-xs text-amber-600">(annulation programmée)</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {s.is_subscribed ? (
                                    <span className="text-sm font-mono">{s.packages_used_this_period || 0} / {s.monthly_quota}</span>
                                  ) : (
                                    <span className="text-slate-400 text-sm">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">
                                  {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString('fr-FR') : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {subscribers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                              Aucun client
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ISM Deals Tab */}
            <TabsContent value="deals">
              <DealsAdmin />
            </TabsContent>

            <TabsContent value="analytics">
              <AnalyticsWidget />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Manual grouping modal */}
      <Dialog open={grouperOpen} onOpenChange={setGrouperOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" /> Grouper {selectedPkgIds.length} colis
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {(() => {
              const selected = packages.filter(p => selectedPkgIds.includes(p.id));
              const totalWeight = selected.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
              return (
                <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                  <p><strong>Client :</strong> {selected[0]?.customer_number} — {selected[0]?.customer_name}</p>
                  <p><strong>Poids total :</strong> {totalWeight.toFixed(2)} kg</p>
                  <p><strong>Destination :</strong> {selected[0]?.destination_country || selected[0]?.recipient_country || 'Non précisé'}</p>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600">Voir les {selected.length} colis</summary>
                    <ul className="mt-2 space-y-0.5">
                      {selected.map(p => (
                        <li key={p.id}>· {p.description} ({p.weight}kg)</li>
                      ))}
                    </ul>
                  </details>
                </div>
              );
            })()}

            <div>
              <Label>Nom du groupage</Label>
              <Input value={grouperName} onChange={e => setGrouperName(e.target.value)} data-testid="grp-name" />
            </div>

            <div>
              <Label>Prix total facturé au client (€) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.01"
                value={grouperPrice}
                onChange={e => setGrouperPrice(e.target.value)}
                placeholder="Ex: 85.00"
                data-testid="grp-price"
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-1">Le client verra ce montant et le paiera en une seule fois.</p>
            </div>

            <div>
              <Label>Note interne (optionnel)</Label>
              <Textarea
                value={grouperNote}
                onChange={e => setGrouperNote(e.target.value)}
                placeholder="Ex: tarif Colissimo négocié, surcharge transporteur..."
                rows={2}
                data-testid="grp-note"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setGrouperOpen(false)} disabled={grouperSubmitting}>Annuler</Button>
              <Button
                onClick={async () => {
                  if (!grouperPrice || parseFloat(grouperPrice) <= 0) {
                    toast.error('Indiquez un prix valide');
                    return;
                  }
                  setGrouperSubmitting(true);
                  try {
                    await api.post('/admin/groups/manual', {
                      package_ids: selectedPkgIds,
                      name: grouperName,
                      total_price: parseFloat(grouperPrice),
                      admin_note: grouperNote,
                    });
                    toast.success('Groupage créé avec succès');
                    setGrouperOpen(false);
                    setSelectedPkgIds([]);
                    await fetchData();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Erreur création groupage');
                  } finally {
                    setGrouperSubmitting(false);
                  }
                }}
                disabled={!grouperPrice || grouperSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="grp-submit"
              >
                {grouperSubmitting ? 'Création...' : 'Créer le groupage'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ship Group Dialog */}
      <Dialog open={groupShipOpen} onOpenChange={setGroupShipOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-600" />
              Expédier le groupage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {groupShipTarget && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                <p className="font-bold text-slate-900">{groupShipTarget.name}</p>
                <p className="text-slate-600">{groupShipTarget.package_ids?.length || 0} colis vers {groupShipTarget.destination_country}</p>
                <p className="text-purple-700 mt-1">→ Tous les colis seront marqués comme expédiés</p>
              </div>
            )}
            <div>
              <Label htmlFor="ship-tracking">Numéro de suivi *</Label>
              <Input
                id="ship-tracking"
                value={groupShipTracking}
                onChange={e => setGroupShipTracking(e.target.value)}
                placeholder="Ex: AB123456789FR"
                data-testid="group-ship-tracking-input"
              />
              <p className="text-xs text-slate-500 mt-1">Ce numéro sera visible par le client et appliqué aux {groupShipTarget?.package_ids?.length || 0} colis du groupage.</p>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setGroupShipOpen(false)} disabled={groupShipSubmitting}>Annuler</Button>
              <Button onClick={confirmShipGroup} disabled={groupShipSubmitting} className="bg-purple-600 hover:bg-purple-700" data-testid="group-ship-confirm">
                {groupShipSubmitting ? 'Expédition...' : 'Confirmer l\'expédition'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Detail Dialog */}
      <Dialog open={groupDetailOpen} onOpenChange={setGroupDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-600" />
              {groupDetailTarget?.name}
            </DialogTitle>
          </DialogHeader>
          {groupDetailTarget && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Client :</span> <strong>{groupDetailTarget.client_name}</strong></div>
                <div><span className="text-slate-500">N° client :</span> <strong className="font-mono text-blue-600">{groupDetailTarget.customer_number}</strong></div>
                <div><span className="text-slate-500">Email :</span> <strong>{groupDetailTarget.client_email}</strong></div>
                <div><span className="text-slate-500">Destination :</span> <strong>{countryNames[groupDetailTarget.destination_country] || groupDetailTarget.destination_country}</strong></div>
                <div><span className="text-slate-500">Poids total :</span> <strong>{groupDetailTarget.total_weight} kg</strong></div>
                <div><span className="text-slate-500">Prix :</span> <strong className="text-purple-700">{formatCurrency(groupDetailTarget.admin_price_total || 0)}</strong></div>
                <div><span className="text-slate-500">Statut :</span> <Badge className={(groupStatusConfig[groupDetailTarget.status] || groupStatusConfig.approved).color}>{(groupStatusConfig[groupDetailTarget.status] || groupStatusConfig.approved).label}</Badge></div>
                <div><span className="text-slate-500">Payé :</span> <strong>{groupDetailTarget.paid ? '✅ Oui' : '⏳ En attente'}</strong></div>
              </div>
              {groupDetailTarget.tracking_number && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  📦 <strong>Suivi :</strong> <span className="font-mono">{groupDetailTarget.tracking_number}</span>
                </div>
              )}
              {groupDetailTarget.admin_price_note && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm italic">
                  📝 « {groupDetailTarget.admin_price_note} »
                </div>
              )}
              <div>
                <h4 className="font-bold text-sm mb-2">Colis dans ce groupage :</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(packages.filter(p => p.group_id === groupDetailTarget.id)).map(p => (
                    <div key={p.id} className="border border-slate-200 rounded p-3 flex justify-between items-center text-sm">
                      <div>
                        <p className="font-medium">{p.description}</p>
                        <p className="text-xs text-slate-500">{p.weight} kg · {p.status}</p>
                      </div>
                      <Badge className={packageStatusConfig[p.status]?.color || 'bg-slate-100'}>
                        {packageStatusConfig[p.status]?.label || p.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  // Helper function for direct lookup
  async function handleCustomerLookupDirect(number) {
    setLookupLoading(true);
    try {
      const res = await adminCustomersAPI.searchByNumber(number);
      setFoundCustomer(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error('Client non trouvé');
    } finally {
      setLookupLoading(false);
    }
  }
}

function StatCard({ icon: Icon, label, value, color, isRevenue }) {
  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className={`font-heading font-bold ${isRevenue ? 'text-lg' : 'text-xl'} text-slate-900`}>
              {value}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AddPackageDialog({ customers, onSuccess, preselectedCustomerId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: preselectedCustomerId || '',
    description: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    destination_country: '',
    photo_url: '',
    notes: ''
  });

  // Update customer_id when preselectedCustomerId changes
  useEffect(() => {
    if (preselectedCustomerId) {
      setFormData(prev => ({ ...prev, customer_id: preselectedCustomerId }));
    }
  }, [preselectedCustomerId]);

  // Auto-fill destination country when customer is selected
  const handleCustomerChange = (customerId) => {
    setFormData(prev => ({ ...prev, customer_id: customerId }));
    const customer = customers.find(c => c.id === customerId);
    if (customer && customer.country) {
      setFormData(prev => ({ ...prev, destination_country: customer.country }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminPackagesAPI.create({
        ...formData,
        weight: parseFloat(formData.weight),
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height)
      });
      toast.success('Colis ajouté ! Le client sera notifié.');
      setOpen(false);
      setFormData({
        customer_id: '', description: '', weight: '', length: '',
        width: '', height: '', destination_country: '', photo_url: '', notes: ''
      });
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du colis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-package-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau colis
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Ajouter un colis</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Client</Label>
            <Select 
              value={formData.customer_id} 
              onValueChange={handleCustomerChange}
            >
              <SelectTrigger data-testid="package-customer-select">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="font-mono text-blue-600">{c.customer_number}</span> - {c.first_name} {c.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              placeholder="Ex: Colis Amazon - vêtements"
              required
              data-testid="package-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Poids (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.weight}
                onChange={(e) => setFormData(p => ({ ...p, weight: e.target.value }))}
                required
                data-testid="package-weight-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Destination</Label>
              <Select 
                value={formData.destination_country} 
                onValueChange={(v) => setFormData(p => ({ ...p, destination_country: v }))}
              >
                <SelectTrigger data-testid="package-country-select">
                  <SelectValue placeholder="Pays" />
                </SelectTrigger>
                <SelectContent>
                  {allCountries.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Longueur (cm)</Label>
              <Input
                type="number"
                value={formData.length}
                onChange={(e) => setFormData(p => ({ ...p, length: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Largeur (cm)</Label>
              <Input
                type="number"
                value={formData.width}
                onChange={(e) => setFormData(p => ({ ...p, width: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Hauteur (cm)</Label>
              <Input
                type="number"
                value={formData.height}
                onChange={(e) => setFormData(p => ({ ...p, height: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>URL de la photo (optionnel)</Label>
            <Input
              value={formData.photo_url}
              onChange={(e) => setFormData(p => ({ ...p, photo_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>

          <div className="space-y-2">
            <Label>Notes (optionnel)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
              placeholder="Notes internes..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Création...' : 'Créer le colis'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditPackageDialog({ pkg, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [formData, setFormData] = useState({
    description: pkg.description,
    weight: pkg.weight,
    length: pkg.length,
    width: pkg.width,
    height: pkg.height,
    destination_country: pkg.destination_country,
    status: pkg.status,
    tracking_url: pkg.tracking_url || '',
    photo_url: pkg.photo_url || '',
    notes: pkg.notes || ''
  });
  
  // Shipping options with editable prices
  const [shippingOptions, setShippingOptions] = useState(
    pkg.available_shipping_options || []
  );

  // Recalculate shipping options when weight or destination changes
  const recalculateOptions = async (weight, destination) => {
    if (!weight || weight <= 0 || !destination) return;
    setRecalculating(true);
    try {
      const res = await shippingCalcAPI.calculateOptions(weight, destination);
      if (res.data.options && res.data.options.length > 0) {
        setShippingOptions(res.data.options);
      }
    } catch (err) {
      console.error('Erreur recalcul options:', err);
    } finally {
      setRecalculating(false);
    }
  };

  const handleWeightChange = (newWeight) => {
    setFormData(p => ({ ...p, weight: newWeight }));
    const w = parseFloat(newWeight);
    if (w > 0) recalculateOptions(w, formData.destination_country);
  };

  const handleDestinationChange = (newDest) => {
    setFormData(p => ({ ...p, destination_country: newDest }));
    const w = parseFloat(formData.weight);
    if (w > 0) recalculateOptions(w, newDest);
  };

  const updateOptionPrice = (optionId, newPrice) => {
    setShippingOptions(prev => prev.map(opt => 
      opt.id === optionId ? { ...opt, price: parseFloat(newPrice) || 0 } : opt
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminPackagesAPI.update(pkg.id, {
        ...formData,
        weight: parseFloat(formData.weight),
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        available_shipping_options: shippingOptions
      });
      toast.success('Colis mis à jour');
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`edit-package-${pkg.id}`}>
          <Edit className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier le colis</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Statut</Label>
            <Select 
              value={formData.status} 
              onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="received">Reçu</SelectItem>
                <SelectItem value="processing">En préparation</SelectItem>
                <SelectItem value="shipped">Expédié</SelectItem>
                <SelectItem value="delivered">Livré</SelectItem>
                <SelectItem value="returned">Retourné</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Lien de suivi (Colissimo, etc.)</Label>
            <Input
              value={formData.tracking_url}
              onChange={(e) => setFormData(p => ({ ...p, tracking_url: e.target.value }))}
              placeholder="https://www.laposte.fr/outils/suivi-de-colis?code=..."
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Poids (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.weight}
                onChange={(e) => handleWeightChange(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Destination</Label>
              <Select 
                value={formData.destination_country} 
                onValueChange={(v) => handleDestinationChange(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allCountries.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Longueur (cm)</Label>
              <Input
                type="number"
                value={formData.length}
                onChange={(e) => setFormData(p => ({ ...p, length: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Largeur (cm)</Label>
              <Input
                type="number"
                value={formData.width}
                onChange={(e) => setFormData(p => ({ ...p, width: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Hauteur (cm)</Label>
              <Input
                type="number"
                value={formData.height}
                onChange={(e) => setFormData(p => ({ ...p, height: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>URL de la photo</Label>
            <Input
              value={formData.photo_url}
              onChange={(e) => setFormData(p => ({ ...p, photo_url: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
            />
          </div>

          {/* Shipping Options Prices */}
          {shippingOptions.length > 0 && !pkg.paid && (
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Tarifs d'expédition (modifiables)
                {recalculating && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
              </Label>
              <p className="text-xs text-slate-500">
                Les prix sont calculés automatiquement. Vous pouvez les ajuster si nécessaire.
              </p>
              <div className="space-y-2">
                {shippingOptions.map((option) => (
                  <div key={option.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-slate-800">{option.name}</p>
                      <p className="text-xs text-slate-500">{option.carrier} • {option.delivery_time}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={option.price}
                        onChange={(e) => updateOptionPrice(option.id, e.target.value)}
                        className="w-24 h-8 text-right font-medium"
                      />
                      <span className="text-sm text-slate-500">€</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {pkg.paid && pkg.shipping_option && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800">Option choisie par le client :</p>
              <p className="text-sm text-green-700">{pkg.shipping_option.name} ({pkg.shipping_option.carrier})</p>
              <p className="text-xs text-green-600">{pkg.shipping_option.delivery_time} - {formatCurrency(pkg.shipping_option.price)}</p>
            </div>
          )}

          {/* Customs Declaration Display */}
          {pkg.customs_declaration && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Déclaration douanière
              </p>
              <div className="text-xs text-amber-700 space-y-1">
                <p><strong>Motif :</strong> {
                  {personal: 'Effets personnels', gift: 'Cadeau', commercial: 'Commercial', return: 'Retour', sample: 'Échantillon'}[pkg.customs_declaration.purpose] || pkg.customs_declaration.purpose
                }</p>
                <p><strong>Valeur totale :</strong> {pkg.customs_declaration.total_value?.toFixed(2)} EUR</p>
                <div className="mt-1 space-y-1">
                  {(pkg.customs_declaration.items || []).map((item, i) => (
                    <div key={i} className="flex justify-between bg-white/60 p-1.5 rounded text-xs">
                      <span>{item.description} (x{item.quantity})</span>
                      <span className="font-medium">{(item.value * item.quantity).toFixed(2)} EUR - {item.weight} kg</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Mise à jour...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeletePackageButton({ packageId, onSuccess }) {
  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce colis ?')) return;
    
    try {
      await adminPackagesAPI.delete(packageId);
      toast.success('Colis supprimé');
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Button variant="ghost" size="icon" onClick={handleDelete} className="text-red-500 hover:text-red-700">
      <Trash2 className="w-4 h-4" />
    </Button>
  );
}

function AddRateDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    zone_name: '',
    countries: [],
    base_price: '',
    price_per_kg: '',
    min_weight: '0',
    max_weight: '30'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await shippingRatesAPI.create({
        ...formData,
        base_price: parseFloat(formData.base_price),
        price_per_kg: parseFloat(formData.price_per_kg),
        min_weight: parseFloat(formData.min_weight),
        max_weight: parseFloat(formData.max_weight)
      });
      toast.success('Tarif créé');
      setOpen(false);
      setFormData({
        zone_name: '', countries: [], base_price: '', price_per_kg: '', min_weight: '0', max_weight: '30'
      });
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const toggleCountry = (code) => {
    setFormData(p => ({
      ...p,
      countries: p.countries.includes(code)
        ? p.countries.filter(c => c !== code)
        : [...p.countries, code]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau tarif
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Créer un tarif</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nom de la zone</Label>
            <Input
              value={formData.zone_name}
              onChange={(e) => setFormData(p => ({ ...p, zone_name: e.target.value }))}
              placeholder="Ex: DOM-TOM, Europe..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Pays ({formData.countries.length} sélectionnés)</Label>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 grid grid-cols-2 gap-1">
              {allCountries.map(c => (
                <label key={c.code} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.countries.includes(c.code)}
                    onChange={() => toggleCountry(c.code)}
                    className="rounded"
                  />
                  <span className="text-sm">{c.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prix de base (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.base_price}
                onChange={(e) => setFormData(p => ({ ...p, base_price: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Prix par kg (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price_per_kg}
                onChange={(e) => setFormData(p => ({ ...p, price_per_kg: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Poids min (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.min_weight}
                onChange={(e) => setFormData(p => ({ ...p, min_weight: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Poids max (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.max_weight}
                onChange={(e) => setFormData(p => ({ ...p, max_weight: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Création...' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRateDialog({ rate, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    zone_name: rate.zone_name,
    countries: rate.countries,
    base_price: rate.base_price,
    price_per_kg: rate.price_per_kg,
    min_weight: rate.min_weight,
    max_weight: rate.max_weight
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await shippingRatesAPI.update(rate.id, {
        ...formData,
        base_price: parseFloat(formData.base_price),
        price_per_kg: parseFloat(formData.price_per_kg),
        min_weight: parseFloat(formData.min_weight),
        max_weight: parseFloat(formData.max_weight)
      });
      toast.success('Tarif mis à jour');
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const toggleCountry = (code) => {
    setFormData(p => ({
      ...p,
      countries: p.countries.includes(code)
        ? p.countries.filter(c => c !== code)
        : [...p.countries, code]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Edit className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier le tarif</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nom de la zone</Label>
            <Input
              value={formData.zone_name}
              onChange={(e) => setFormData(p => ({ ...p, zone_name: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Pays ({formData.countries.length} sélectionnés)</Label>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 grid grid-cols-2 gap-1">
              {allCountries.map(c => (
                <label key={c.code} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.countries.includes(c.code)}
                    onChange={() => toggleCountry(c.code)}
                    className="rounded"
                  />
                  <span className="text-sm">{c.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prix de base (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.base_price}
                onChange={(e) => setFormData(p => ({ ...p, base_price: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Prix par kg (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price_per_kg}
                onChange={(e) => setFormData(p => ({ ...p, price_per_kg: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Poids min (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.min_weight}
                onChange={(e) => setFormData(p => ({ ...p, min_weight: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Poids max (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.max_weight}
                onChange={(e) => setFormData(p => ({ ...p, max_weight: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Mise à jour...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRateButton({ rateId, onSuccess }) {
  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) return;
    
    try {
      await shippingRatesAPI.delete(rateId);
      toast.success('Tarif supprimé');
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Button variant="ghost" size="icon" onClick={handleDelete} className="text-red-500 hover:text-red-700">
      <Trash2 className="w-4 h-4" />
    </Button>
  );
}
